import imageUrlBuilder from '@sanity/image-url'
import clsx from 'clsx'
import Image from 'next/image'
import Link from 'next/link'
import { memo, useCallback, useEffect, useRef, useState } from 'react'
import { Clock } from 'lucide-react'
import { dataset, projectId } from '@/sanity/env'
import LearnPagination from './pagination'

const builder = imageUrlBuilder({ dataset, projectId })

function urlFor(source) {
  return builder.image(source)
}

const PostList = ({ 
  posts, 
  currentPage, 
  totalPages, 
  baseUrl = '/resources',
  activeCategory = null,
  postsPerPage = 15,
}) => {
  const [visiblePosts, setVisiblePosts] = useState(posts)
  const [nextOffset, setNextOffset] = useState(
    (currentPage - 1) * postsPerPage + posts.length,
  )
  const [hasMore, setHasMore] = useState(currentPage < totalPages)
  const [isLoading, setIsLoading] = useState(false)
  const [loadError, setLoadError] = useState(null)
  const loadMoreRef = useRef(null)
  const loadingRef = useRef(false)
  const aosRef = useRef(null)

  useEffect(() => {
    let isMounted = true

    import('aos').then(({ default: AOS }) => {
      if (!isMounted) return

      aosRef.current = AOS
      AOS.init({
        duration: 150,
        easing: 'ease-out',
        once: false,
        offset: 80,
      })
      AOS.refresh()
    })

    return () => {
      isMounted = false
      aosRef.current = null
    }
  }, [])

  useEffect(() => {
    setVisiblePosts(posts)
    setNextOffset((currentPage - 1) * postsPerPage + posts.length)
    setHasMore(currentPage < totalPages)
    setIsLoading(false)
    setLoadError(null)
    loadingRef.current = false
  }, [activeCategory, currentPage, posts, postsPerPage, totalPages])

  const loadMorePosts = useCallback(async () => {
    if (loadingRef.current || !hasMore) return

    loadingRef.current = true
    setIsLoading(true)
    setLoadError(null)

    const params = new URLSearchParams({
      offset: String(nextOffset),
      limit: String(postsPerPage),
    })

    if (activeCategory) {
      params.set('slug', activeCategory)
    }

    const endpoint = activeCategory
      ? '/api/load-cat-posts'
      : '/api/load-more-posts'

    try {
      const response = await fetch(`${endpoint}?${params.toString()}`)

      if (!response.ok) {
        throw new Error('Unable to load more resources')
      }

      const result = await response.json()
      const nextPosts = result.posts || []

      setVisiblePosts(currentPosts => {
        const currentPostIds = new Set(currentPosts.map(post => post._id))
        const uniqueNextPosts = nextPosts.filter(
          post => !currentPostIds.has(post._id),
        )

        return [...currentPosts, ...uniqueNextPosts]
      })
      setNextOffset(offset => offset + nextPosts.length)
      setHasMore(result.hasMore && nextPosts.length > 0)
    } catch (error) {
      setLoadError(error.message)
    } finally {
      loadingRef.current = false
      setIsLoading(false)
    }
  }, [activeCategory, hasMore, nextOffset, postsPerPage])

  useEffect(() => {
    const loadMoreElement = loadMoreRef.current

    if (!loadMoreElement || !hasMore || loadError) return undefined

    if (!('IntersectionObserver' in window)) {
      loadMorePosts()
      return undefined
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          loadMorePosts()
        }
      },
      {
        rootMargin: '0px 0px 1200px 0px',
      },
    )

    observer.observe(loadMoreElement)

    return () => observer.disconnect()
  }, [hasMore, loadError, loadMorePosts])

  useEffect(() => {
    if (!aosRef.current) return undefined

    const refreshFrame = window.requestAnimationFrame(() => {
      aosRef.current?.refreshHard()
    })

    return () => window.cancelAnimationFrame(refreshFrame)
  }, [visiblePosts.length])

  return (
    <div className="resource-list w-full">
      <ul className="grid w-full gap-8">
        {visiblePosts.map((post, index) => {
          return (
            <li
              key={post._id}
              className="w-full"
              data-aos="fade-up"
              data-aos-offset="-120"
            >
              <div className="flex w-full flex-col gap-4 lg:flex-row lg:gap-6">
                <div className="relative mr-auto shrink-0 sm:h-[114px] sm:w-[342px]">
                  {post.mainImage
                    ? <Link href={`/resources/${post.slug.current}`}>
                        <Image
                          src={urlFor(post.mainImage).url()}
                          width={600}
                          height={400}
                          sizes="(min-width: 640px) 342px, calc(100vw - 40px)"
                          alt={post.title}
                          className="h-full w-full object-cover"
                          loading={index < 3 ? "eager" : "lazy"}
                          priority={index < 3}
                          placeholder="blur"
                          blurDataURL="data:image/jpeg;base64,/9j/4AAQSkZJRgABAQAAAQABAAD/2wBDAAYEBQYFBAYGBQYHBwYIChAKCgkJChQODwwQFxQYGBcUFhYaHSUfGhsjHBYWICwgIyYnKSopGR8tMC0oMCUoKSj/2wBDAQcHBwoIChMKChMoGhYaKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCj/wAARCAABAAEDASIAAhEBAxEB/8QAFQABAQAAAAAAAAAAAAAAAAAAAAv/xAAhEAACAQMDBQAAAAAAAAAAAAABAgMABAUGIWGRkqGx0f/EABUBAQEAAAAAAAAAAAAAAAAAAAMF/8QAGhEAAgIDAAAAAAAAAAAAAAAAAAECEgMRkf/aAAwDAQACEQMRAD8AltJagyeH0AthI5xdrLcNM91BF5pX2HaH9bcfaSXWGaRmknyJckliyjqTzSlT54b6bk+h0R//2Q=="
                        />
                      </Link>
                    : <div className="h-[114px] w-[342px] bg-neutral-100"></div>}
                </div>
                <div className="w-full">
                  <div className="mb-2.5 flex flex-wrap items-center gap-2.5">
                    <div className="flex flex-wrap gap-1.5">
                      {post.categories
                        .map((category) => {
                          return (
                            <Link
                              href={`/resources/category/${category.slug.current}`}
                              key={category._id}
                              className={clsx(
                                'rounded-full px-2 py-1 text-xs leading-none hover:!underline transition-colors',
                                activeCategory === category.slug.current
                                  ? 'bg-blue-100 text-blue-800'
                                  : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200',
                              )}
                            >
                              {category.title}
                            </Link>
                          )
                        })
                        .slice(0, 2)}
                    </div>
                    <div className="flex items-center gap-2 text-xs text-neutral-500">
                      <div className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        <span>{post.readingTime} min read</span>
                      </div>
                    </div>
                  </div>
                  <Link href={`/resources/${post.slug.current}`}>
                    <h2 className="mb-1 text-xl font-bold hover:text-blue-600 transition-colors">
                      {post.title}
                    </h2>
                  </Link>
                  <p className="line-clamp-2 text-neutral-500 leading-relaxed">
                    {post.excerpt}
                  </p>
                </div>
              </div>
            </li>
          )
        })}
      </ul>
      
      {hasMore ? (
        <div
          ref={loadMoreRef}
          className="flex min-h-20 items-center justify-center"
          aria-live="polite"
        >
          {isLoading ? (
            <span className="text-sm text-neutral-500">
              Loading more resources…
            </span>
          ) : null}
          {loadError ? (
            <button
              type="button"
              className="text-sm font-semibold text-blue-600 underline underline-offset-4 hover:text-blue-800"
              onClick={loadMorePosts}
            >
              Try loading more resources again
            </button>
          ) : null}
        </div>
      ) : null}

      <noscript>
        <LearnPagination
          currentPage={currentPage}
          totalPages={totalPages}
          baseUrl={baseUrl}
        />
      </noscript>
    </div>
  )
}

export default memo(PostList)
